import React, { useState, useEffect } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

// Import Components
import VECIHeader from '../../Components/SurveyVECI/VECIHeader';
import VECIAccessDenied from '../../Components/SurveyVECI/VECIAccessDenied';
import VECIIdentitas from '../../Components/SurveyVECI/VECIIdentitas';
import VECIProfilResponden from '../../Components/SurveyVECI/VECIProfilResponden';
import VECIInfoBumdes from '../../Components/SurveyVECI/VECIInfoBumdes';
import VECIPesaing from '../../Components/SurveyVECI/VECIPesaing';
import VECIInstructions from '../../Components/SurveyVECI/VECIInstructions';
import VECIActiveQuestion from '../../Components/SurveyVECI/VECIActiveQuestion';
import VECIReview from '../../Components/SurveyVECI/VECIReview';
import VECISurveyCompleted from '../../Components/SurveyVECI/VECISurveyCompleted';
import api from '../../utils/api';
import { showToast } from '../../Components/ToastNotifications';
import { useAuth } from '../../context/AuthContext';

const STORAGE_KEY = 'bumdes_veci_t0_survey_draft';

const getStepLabel = (step) => {
  const titles = [
    '',
    'I. Identitas & Validasi',
    'II. Profil Direktur BUMDesa',
    'III. Informasi BUMDes',
    'IV. Profiling Pesaing',
    'V. Instruksi & Panduan',
    'VI. Instrumen VECI',
    'VII. Konteks Strategis',
    'VIII. Review Akhir',
  ];
  return titles[step];
};

const BumdesVeci = () => {
  const navigate = useNavigate();
  const { authUser } = useAuth();
  const [step, setStep] = useState(1);

  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [isAuthorized, setIsAuthorized] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [identity, setIdentity] = useState(null);
  const [questions, setQuestions] = useState({
    op: [],
    ad: [],
    mp: [],
    s: [],
    e: [],
  });
  const [surveyId, setSurveyId] = useState(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [sessionStartTime, setSessionStartTime] = useState(null);
  const [anomalyAlert, setAnomalyAlert] = useState({
    show: false,
    anomalies: [],
  });
  const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);
  const [isAnomalyConfirmed, setIsAnomalyConfirmed] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [isSubmittingSurvey, setIsSubmittingSurvey] = useState(false);
  const [isValidatingAnomalies, setIsValidatingAnomalies] = useState(false);
  const [authMessage, setAuthMessage] = useState(null);

  const handleValidateAnomalies = async (sectionCode) => {
    // Disable anomaly validation for BUMDes
    return true;
  };

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    getValues,
    control,
    trigger,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      respondent: { gender: 'Male', positionAtBumdes: 'Direktur' },
      businessInfo: {
        mainSector: 'Agriculture',
        sourceOfCapital: [],
        productsServices: [''],
        secondarySector: '',
        secondaryProductsServices: [],
      },
      competitors: [
        {
          name: '',
          village: '',
          subDistrict: '',
          regency: '',
          province: '',
          shortProfile: '',
          reasons: [],
          otherReason: '',
          isStrongest: true,
        },
      ],
      answers: {},
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'businessInfo.productsServices',
  });

  const {
    fields: secondaryFields,
    append: secondaryAppend,
    remove: secondaryRemove,
  } = useFieldArray({
    control,
    name: 'businessInfo.secondaryProductsServices',
  });

  const totalSteps = 8;
  const instrumentQuestions = [
    ...questions.op,
    ...questions.ad,
    ...questions.mp,
  ];
  const contextQuestions = [
    ...questions.s.filter((q) => {
      if (q.questionCode === 'O2')
        return watch('answers.O1.optionValue') === 'Ya';
      if (q.questionCode === 'O4')
        return watch('answers.O3.optionValue') === 'Ya';
      return true;
    }),
    ...questions.e,
  ];
  const totalInstrumentCount = instrumentQuestions.length;
  const totalContextCount = contextQuestions.length;

  const watchAllFields = watch();

  // Load draft from localStorage on mount
  useEffect(() => {
    const savedDraft = localStorage.getItem(STORAGE_KEY);
    if (savedDraft) {
      try {
        const parsedDraft = JSON.parse(savedDraft);
        reset(parsedDraft);
        if (parsedDraft.step) setStep(parsedDraft.step);
        if (parsedDraft.currentQuestionIndex !== undefined)
          setCurrentQuestionIndex(parsedDraft.currentQuestionIndex);
      } catch (error) {
        console.error('Failed to parse VECI draft:', error);
      }
    }
  }, [reset]);

  // Save to localStorage on changes
  useEffect(() => {
    if (!isLoading) {
      const dataToSave = { ...watchAllFields, step, currentQuestionIndex };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(dataToSave));
    }
  }, [watchAllFields, step, currentQuestionIndex, isLoading]);

  const totalOPCount = questions.op.length;
  const totalADCount = questions.ad.length;
  const totalMPCount = questions.mp.length;

  const isOpComplete =
    questions.op.length > 0 &&
    questions.op.every((q) => watch(`answers.${q.questionCode}.score`));

  const isAdComplete =
    isOpComplete &&
    questions.ad.length > 0 &&
    questions.ad.every((q) => watch(`answers.${q.questionCode}.score`));

  const isMpComplete =
    isAdComplete &&
    questions.mp.length > 0 &&
    questions.mp.every((q) => watch(`answers.${q.questionCode}.score`));

  const isSComplete =
    isMpComplete &&
    questions.s.length > 0 &&
    questions.s.every((q) => {
      if (q.questionCode === 'O2' && watch('answers.O1.optionValue') !== 'Ya')
        return true;
      if (q.questionCode === 'O4' && watch('answers.O3.optionValue') !== 'Ya')
        return true;
      return (
        watch(`answers.${q.questionCode}.optionValue`) ||
        watch(`answers.${q.questionCode}.score`)
      );
    });

  const isEComplete =
    isSComplete &&
    questions.e.length > 0 &&
    questions.e.every((q) => watch(`answers.${q.questionCode}.score`));

  const [sectionAttempted, setSectionAttempted] = useState(null);

  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    const fetchInitialData = async () => {
      setIsLoading(true);
      try {
        let idData = null;
        try {
          const identityRes = await api.getVECIIdentity();
          if (identityRes.status === 'success') {
            idData = identityRes.data.surveyIdentity;

            // Force phase to T0 for this BUMDes portal page
            if (idData.surveyContext) {
              idData.surveyContext.phase = 'T0';
            }

            setIdentity(idData);
            setIsAuthorized(idData.surveyContext.isAuthorized);
            setIsSubmitted(idData.surveyContext.isSubmitted);
            if (idData.surveyContext.existingSurveyId) {
              setSurveyId(idData.surveyContext.existingSurveyId);
            }
            // Set startTime: use existing draft time or current time for new survey
            setSessionStartTime(
              idData.surveyContext.startTime || new Date().toISOString(),
            );
          } else {
            setIsAuthorized(false);
            setAuthMessage(identityRes.message || 'Akses ditutup.');
          }
        } catch (identityError) {
          console.error('Identity Fetch Error:', identityError);
          setIsAuthorized(false);
          setAuthMessage(identityError.message || 'Akses ditutup.');
        }

        // Fetch questions only if identity succeeded
        if (idData) {
          try {
            const questionsRes = await api.getVECIQuestions();
            if (questionsRes.status === 'success') {
              const qs = questionsRes.data.questions;
              setQuestions({
                op: qs.filter((q) => q.section === 'OP'),
                ad: qs.filter((q) => q.section === 'AD'),
                mp: qs.filter((q) => q.section === 'MP'),
                s: qs.filter((q) => q.section === 'S'),
                e: qs.filter((q) => q.section === 'E'),
              });
            }
          } catch (questionsError) {
            console.error('Questions Fetch Error:', questionsError);
          }

          // Autofill Profil Direktur BUMDesa from BUMDes owner data (still editable by user)
          try {
            const autofillRes = await api.getBumdesPreSurveyAutofill();
            const autofillData = autofillRes?.data;
            if (autofillData) {
              if (
                !getValues('respondent.respondentName') &&
                authUser?.fullName
              ) {
                setValue('respondent.respondentName', authUser.fullName);
              }
              if (
                !getValues('respondent.phoneNumber') &&
                autofillData.whatsapp
              ) {
                setValue('respondent.phoneNumber', autofillData.whatsapp);
              }
              if (
                !getValues('respondent.dateOfBirth') &&
                autofillData.birthDate
              ) {
                setValue('respondent.dateOfBirth', autofillData.birthDate);
              }
              if (
                !getValues('respondent.positionAtBumdes') &&
                (autofillData.position || autofillData.role)
              ) {
                setValue(
                  'respondent.positionAtBumdes',
                  autofillData.position || autofillData.role,
                );
              }
              if (
                !getValues('respondent.residentialAddress') &&
                autofillData.residentialAddress
              ) {
                setValue(
                  'respondent.residentialAddress',
                  autofillData.residentialAddress,
                );
              }
              if (
                !getValues('businessInfo.initialCapital') &&
                autofillData.initialCapital
              ) {
                setValue(
                  'businessInfo.initialCapital',
                  autofillData.initialCapital,
                );
              }
              if (
                !getValues('businessInfo.currentCapital') &&
                autofillData.currentCapital
              ) {
                setValue(
                  'businessInfo.currentCapital',
                  autofillData.currentCapital,
                );
              }
              if (
                !getValues('businessInfo.monthlyGrossIncome') &&
                autofillData.monthlyGrossIncome
              ) {
                setValue(
                  'businessInfo.monthlyGrossIncome',
                  autofillData.monthlyGrossIncome,
                );
              }
              if (
                !getValues('businessInfo.annualGrossIncome') &&
                autofillData.annualGrossIncome
              ) {
                setValue(
                  'businessInfo.annualGrossIncome',
                  autofillData.annualGrossIncome,
                );
              }
            }
          } catch (autofillError) {
            console.error('Respondent Autofill Fetch Error:', autofillError);
          }
        }
      } catch (err) {
        console.error('Initial Fetch Error:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchInitialData();
  }, []);

  useEffect(() => {
    let interval;
    if (step === 6 || step === 7) {
      interval = setInterval(() => {
        setElapsed((prev) => prev + 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [step, currentQuestionIndex]);

  const recordTime = () => {
    if ((step === 6 || step === 7) && activeQuestion) {
      const prevDuration =
        getValues(`answers.${activeQuestion.questionCode}.duration`) || 0;
      setValue(
        `answers.${activeQuestion.questionCode}.duration`,
        prevDuration + elapsed,
      );
      setElapsed(0);
    }
  };

  const jumpToSection = (section) => {
    recordTime();

    if (section === 'AD' && !isOpComplete) {
      showToast('Selesaikan dimensi Operasional terlebih dahulu!', 'warning');
      return;
    }
    if (section === 'MP' && !isAdComplete) {
      showToast('Selesaikan dimensi Adaptabilitas terlebih dahulu!', 'warning');
      return;
    }
    if (section === 'S' && !isMpComplete) {
      showToast('Selesaikan dimensi Kinerja Pasar terlebih dahulu!', 'warning');
      return;
    }

    if (section === 'E' && !isSComplete) {
      showToast(
        'Selesaikan dimensi Konteks Strategis terlebih dahulu!',
        'warning',
      );
      return;
    }

    if (section === 'OP') {
      setStep(6);
      setCurrentQuestionIndex(0);
      setSectionAttempted(null);
    } else if (section === 'AD') {
      setStep(6);
      setCurrentQuestionIndex(totalOPCount);
      setSectionAttempted(null);
    } else if (section === 'MP') {
      setStep(6);
      setCurrentQuestionIndex(totalOPCount + totalADCount);
      setSectionAttempted(null);
    } else if (section === 'S') {
      setStep(7);
      setCurrentQuestionIndex(0);
      setSectionAttempted(null);
    } else if (section === 'E') {
      setStep(7);
      const sCount = contextQuestions.filter((q) => q.section === 'S').length;
      setCurrentQuestionIndex(sCount);
      setSectionAttempted(null);
    }
  };

  const jumpToQuestion = (questionCode) => {
    recordTime();

    if (questionCode.startsWith('AD') && !isOpComplete) {
      showToast('Selesaikan dimensi Operasional terlebih dahulu!', 'warning');
      return;
    }
    if (questionCode.startsWith('MP') && !isAdComplete) {
      showToast('Selesaikan dimensi Adaptabilitas terlebih dahulu!', 'warning');
      return;
    }

    const isContext = contextQuestions.some(
      (q) => q.questionCode === questionCode,
    );

    if (isContext) {
      if (!isMpComplete) {
        showToast(
          'Selesaikan dimensi Kinerja Pasar terlebih dahulu!',
          'warning',
        );
        return;
      }
      const qObj = contextQuestions.find(
        (q) => q.questionCode === questionCode,
      );
      if (qObj?.section === 'E' && !isSComplete) {
        showToast(
          'Selesaikan dimensi Konteks Strategis terlebih dahulu!',
          'warning',
        );
        return;
      }
      setStep(7);
      const idx = contextQuestions.findIndex(
        (q) => q.questionCode === questionCode,
      );
      if (idx !== -1) setCurrentQuestionIndex(idx);
    } else {
      setStep(6);
      const idx = instrumentQuestions.findIndex(
        (q) => q.questionCode === questionCode,
      );
      setCurrentQuestionIndex(idx);
    }
  };

  const birthDate = watch('respondent.dateOfBirth');
  useEffect(() => {
    if (birthDate) {
      const birth = new Date(birthDate);
      const today = new Date();
      let age = today.getFullYear() - birth.getFullYear();
      if (
        today.getMonth() < birth.getMonth() ||
        (today.getMonth() === birth.getMonth() &&
          today.getDate() < birth.getDate())
      )
        age--;
      setValue('respondent.age', age);
    }
  }, [birthDate, setValue]);

  const nextStep = async () => {
    recordTime();
    let fieldsToValidate = [];
    if (step === 2) fieldsToValidate = ['respondent'];
    if (step === 3) fieldsToValidate = ['businessInfo'];
    if (step === 4) fieldsToValidate = ['competitors', 'competitiveContext'];

    if (fieldsToValidate.length > 0) {
      const isValid = await trigger(fieldsToValidate);
      if (!isValid) return;
    }

    if (step === 4) {
      const currentCompetitors = getValues('competitors');
      const hasStrongest = currentCompetitors.some((comp) => comp.isStrongest);
      if (!hasStrongest) {
        showToast('Wajib memilih minimal 1 pesaing sebagai Terkuat', 'warning');
        return;
      }
    }

    setStep((prev) => prev + 1);
  };

  const prevStep = () => {
    recordTime();
    setStep((prev) => prev - 1);
  };

  const nextQuestion = async () => {
    recordTime();
    // Rule for T0 survey phase: Note field is never required or validated because it is hidden
    if (step === 6 || step === 7) {
      const isContext = activeQuestion.type !== 'scale';
      const fieldName = isContext
        ? `answers.${activeQuestion.questionCode}.optionValue`
        : `answers.${activeQuestion.questionCode}.score`;

      const fieldsToTrigger = [fieldName];
      const isValid = await trigger(fieldsToTrigger);
      if (!isValid) return;
    }

    if (step === 6) {
      const currentQ = instrumentQuestions[currentQuestionIndex];
      const isLastOP =
        currentQ.questionCode.startsWith('OP') &&
        currentQuestionIndex === totalOPCount - 1;
      const isLastAD =
        currentQ.questionCode.startsWith('AD') &&
        currentQuestionIndex === totalOPCount + totalADCount - 1;
      const isLastMP =
        currentQ.questionCode.startsWith('MP') &&
        currentQuestionIndex === totalInstrumentCount - 1;

      if (isLastOP && !isOpComplete) {
        showToast(
          'Selesaikan seluruh dimensi Operasional terlebih dahulu!',
          'warning',
        );
        setSectionAttempted('OP');
        return;
      }
      if (isLastAD && !isAdComplete) {
        showToast(
          'Selesaikan seluruh dimensi Adaptabilitas terlebih dahulu!',
          'warning',
        );
        setSectionAttempted('AD');
        return;
      }
      if (isLastMP && !isMpComplete) {
        showToast(
          'Selesaikan seluruh dimensi Kinerja Pasar terlebih dahulu!',
          'warning',
        );
        setSectionAttempted('MP');
        return;
      }

      // Anomaly Validation for OP and AD
      if (isLastOP) {
        const canProceed = await handleValidateAnomalies('OP');
        if (!canProceed) return;
      }

      if (isLastAD) {
        const canProceed = await handleValidateAnomalies('AD');
        if (!canProceed) return;
      }

      if (currentQuestionIndex < totalInstrumentCount - 1) {
        setCurrentQuestionIndex((prev) => prev + 1);
      } else {
        nextStep();
        setCurrentQuestionIndex(0);
        setSectionAttempted(null);
      }
    } else if (step === 7) {
      if (currentQuestionIndex < totalContextCount - 1) {
        setCurrentQuestionIndex((prev) => prev + 1);
      } else {
        if (!isSComplete || !isEComplete) {
          showToast(
            'Selesaikan seluruh dimensi Konteks Strategis & Keterhubungan terlebih dahulu!',
            'warning',
          );
          setSectionAttempted(!isSComplete ? 'S' : 'E');
          return;
        }
        nextStep();
        setSectionAttempted(null);
      }
    }
  };

  const prevQuestion = () => {
    recordTime();
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex((prev) => prev - 1);
    } else {
      prevStep();
      if (step === 7) setCurrentQuestionIndex(totalInstrumentCount - 1);
    }
  };

  const formatPayload = (data, status) => {
    const formattedAnswers = Object.entries(data.answers || {}).map(
      ([questionCode, answerData]) => ({
        questionCode,
        ...answerData,
      }),
    );

    const formattedCompetitiveContext = Object.entries(
      data.competitiveContext || {},
    ).map(([questionCode, contextData]) => ({
      questionCode,
      ...contextData,
    }));

    const formattedCompetitors = (data.competitors || []).map((comp) => {
      if (
        comp.reasons &&
        comp.reasons.includes('Alasan lain yang disebutkan informan')
      ) {
        return {
          ...comp,
          reasons: comp.reasons.map((r) =>
            r === 'Alasan lain yang disebutkan informan'
              ? comp.otherReason || r
              : r,
          ),
        };
      }
      return comp;
    });

    return {
      phase: 'T0',
      status: status,
      startTime: sessionStartTime,
      institutionId: identity?.bumdes?.institutionId,
      ...data,
      competitors: formattedCompetitors,
      answers: formattedAnswers,
      competitiveContext: formattedCompetitiveContext,
    };
  };

  const onSubmit = async (data, isConfirmed = false, isForced = false) => {
    if (!isConfirmed) {
      setShowSubmitConfirm(true);
      return;
    }

    setShowSubmitConfirm(false);
    setIsSubmittingSurvey(true);
    try {
      const payload = formatPayload(data, 'SUBMITTED');
      payload.isForcedSubmit = true; // Always bypass anomaly validation for BUMDes
      const response = await api.submitVECISurvey(payload);

      if (response.status === 'success') {
        showToast('Survei VECI Berhasil Disubmit!', 'success');
        localStorage.removeItem(STORAGE_KEY);
        setIsSubmitted(true);
      } else if (response.anomalies) {
        // Handle Anomaly Detection
        setAnomalyAlert({
          show: true,
          anomalies: response.anomalies,
        });
        setIsAnomalyConfirmed(false);
        showToast('Terdeteksi anomali pada pengisian survei.', 'warning');
      } else {
        showToast(`Gagal: ${response.message}`, 'danger');
      }
    } catch (error) {
      console.error('Submit Error:', error);
      showToast('Terjadi kesalahan saat mengirim survei.', 'danger');
    } finally {
      setIsSubmittingSurvey(false);
    }
  };

  const handleForceSubmit = () => {
    setAnomalyAlert({ show: false, anomalies: [] });
    setIsAnomalyConfirmed(false);
    handleSubmit((data) => onSubmit(data, true, true))();
  };

  const handleAnomalyAction = () => {
    if (anomalyAlert.mode === 'SECTION') {
      setAnomalyAlert({ show: false, anomalies: [] });
      setIsAnomalyConfirmed(false);
      setCurrentQuestionIndex((prev) => prev + 1);
    } else {
      handleForceSubmit();
    }
  };

  const handleSaveDraft = async () => {
    const data = getValues();
    setIsSavingDraft(true);
    try {
      const payload = formatPayload(data, 'DRAFT');
      let response;

      if (surveyId) {
        response = await api.updateDraftVECISurvey(surveyId, payload);
      } else {
        response = await api.saveDraftVECISurvey(payload);
        if (response.status === 'success' && response.data.surveyId) {
          setSurveyId(response.data.surveyId);
        }
      }

      if (response.status === 'success') {
        showToast('Draft survei VECI berhasil disimpan.', 'success');
        localStorage.removeItem(STORAGE_KEY);
      } else {
        showToast(`Gagal menyimpan draft: ${response.message}`, 'danger');
      }
    } catch (error) {
      console.error('Draft Error:', error);
      showToast('Terjadi kesalahan saat menyimpan draft.', 'danger');
    } finally {
      setIsSavingDraft(false);
    }
  };

  const activeQuestion =
    step === 6
      ? instrumentQuestions[currentQuestionIndex]
      : step === 7
        ? contextQuestions[currentQuestionIndex]
        : null;

  if (isLoading) {
    return (
      <div className='d-flex justify-content-center align-items-center vh-100'>
        <div
          className='spinner-border text-primary'
          role='status'
        >
          <span className='visually-hidden'>Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className='container py-4'>
      <div className='row justify-content-center'>
        <div className='col-lg-10'>
          {!isSubmitted && (
            <VECIHeader
              currentStepLabel={getStepLabel(step)}
              step={step}
              totalSteps={totalSteps}
              progress={(step / totalSteps) * 100}
            />
          )}

          {!isAuthorized ? (
            <VECIAccessDenied message={authMessage} />
          ) : isSubmitted ? (
            <VECISurveyCompleted dashboardUrl='/bumdes' />
          ) : (
            <form onSubmit={handleSubmit(onSubmit)}>
              {step === 1 && <VECIIdentitas identity={identity} />}
              {step === 2 && (
                <VECIProfilResponden
                  register={register}
                  errors={errors}
                  watch={watch}
                  control={control}
                  setValue={setValue}
                />
              )}
              {step === 3 && (
                <VECIInfoBumdes
                  register={register}
                  errors={errors}
                  control={control}
                  setValue={setValue}
                  fields={fields}
                  append={append}
                  remove={remove}
                  secondaryFields={secondaryFields}
                  secondaryAppend={secondaryAppend}
                  secondaryRemove={secondaryRemove}
                />
              )}
              {step === 4 && (
                <VECIPesaing
                  register={register}
                  errors={errors}
                  watch={watch}
                  setValue={setValue}
                  control={control}
                  getValues={getValues}
                />
              )}
              {step === 5 && <VECIInstructions />}

              {(step === 6 || step === 7) && activeQuestion && (
                <div>
                  <div className='card border-0 shadow-sm rounded-4 mb-4 p-2 bg-light bg-opacity-50'>
                    <div className='nav nav-pills nav-fill gap-2'>
                      {['OP', 'AD', 'MP', 'S', 'E'].map((sec) => {
                        const isActive = activeQuestion?.section === sec;
                        const isDisabled =
                          (sec === 'AD' && !isOpComplete) ||
                          (sec === 'MP' && !isAdComplete) ||
                          (sec === 'S' && !isMpComplete) ||
                          (sec === 'E' && !isSComplete);

                        return (
                          <button
                            key={sec}
                            type='button'
                            disabled={isDisabled}
                            className={`nav-link rounded-pill py-2 px-1 px-md-3 fw-bold transition-all border-0 ${isActive ? 'active' : isDisabled ? 'text-muted bg-white opacity-50 cursor-not-allowed' : 'text-muted bg-white'}`}
                            onClick={() => jumpToSection(sec)}
                            style={{
                              fontSize: '0.85rem',
                              cursor: isDisabled ? 'not-allowed' : 'pointer',
                            }}
                          >
                            <span className='d-none d-md-inline'>
                              {sec === 'OP'
                                ? 'Operasional'
                                : sec === 'AD'
                                  ? 'Adaptabilitas'
                                  : sec === 'MP'
                                    ? 'Kinerja Pasar'
                                    : sec === 'S'
                                      ? 'Strategis'
                                      : 'Keterhubungan'}
                            </span>
                            <span className='d-md-none'>{sec}</span>
                            {isDisabled && (
                              <i className='bi bi-lock-fill ms-1 extra-small'></i>
                            )}
                          </button>
                        );
                      })}
                    </div>

                    <div className='d-flex flex-wrap gap-2 mt-3 justify-content-center border-top pt-3'>
                      {(activeQuestion?.section === 'OP'
                        ? questions.op
                        : activeQuestion?.section === 'AD'
                          ? questions.ad
                          : activeQuestion?.section === 'MP'
                            ? questions.mp
                            : activeQuestion?.section === 'S'
                              ? contextQuestions.filter(
                                  (q) => q.section === 'S',
                                )
                              : activeQuestion?.section === 'E'
                                ? contextQuestions.filter(
                                    (q) => q.section === 'E',
                                  )
                                : []
                      ).map((q, i) => {
                        const isContext = q.type !== 'scale';
                        const currentSec = q.section;
                        const fieldName = isContext
                          ? `answers.${q.questionCode}.optionValue`
                          : `answers.${q.questionCode}.score`;
                        const isAnswered = watch(fieldName);
                        const isError =
                          sectionAttempted === currentSec && !isAnswered;

                        return (
                          <button
                            key={q.questionCode}
                            type='button'
                            className={`btn btn-sm rounded-circle d-flex align-items-center justify-content-center transition-all ${activeQuestion?.questionCode === q.questionCode ? 'btn-primary shadow' : isAnswered ? 'btn-success bg-success bg-opacity-10 text-success' : isError ? 'btn-danger bg-danger bg-opacity-10 text-danger border-danger' : 'btn-white text-muted border'}`}
                            style={{
                              width: '32px',
                              height: '32px',
                              fontSize: '0.75rem',
                            }}
                            onClick={() => jumpToQuestion(q.questionCode)}
                          >
                            {i + 1}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <VECIActiveQuestion
                    key={activeQuestion.questionCode}
                    step={step}
                    stepLabel={getStepLabel(step)}
                    currentQuestionIndex={currentQuestionIndex}
                    totalQuestionsCount={
                      step === 6 ? totalInstrumentCount : totalContextCount
                    }
                    question={activeQuestion}
                    register={register}
                    watch={watch}
                    setValue={setValue}
                    onNext={nextQuestion}
                    elapsed={elapsed}
                    errors={errors}
                    isValidating={isValidatingAnomalies}
                    sectionAttempted={sectionAttempted}
                    jumpToSection={jumpToSection}
                    jumpToQuestion={jumpToQuestion}
                    questions={questions}
                    isOpComplete={isOpComplete}
                    isAdComplete={isAdComplete}
                    isMpComplete={isMpComplete}
                    strongestCompetitorName={
                      watch('competitors')?.find((c) => c.isStrongest)?.name ||
                      ''
                    }
                    hideNotes={true} // Hardcode to true since this is the T0 survey
                  />
                </div>
              )}

              {step === 8 && (
                <VECIReview
                  watch={watch}
                  questions={questions}
                />
              )}

              <div className='d-flex flex-column-reverse flex-sm-row justify-content-between align-items-stretch align-items-sm-center gap-2 mt-4 border-top pt-3'>
                {step > 1 && (
                  <button
                    type='button'
                    className='btn btn-outline-secondary px-4 py-2 rounded-3'
                    onClick={step === 6 || step === 7 ? prevQuestion : prevStep}
                  >
                    <i className='bi bi-arrow-left me-2'></i> Sebelumnya
                  </button>
                )}
                {step < 8 ? (
                  step !== 6 &&
                  step !== 7 && (
                    <button
                      type='button'
                      className='btn btn-primary px-5 py-2 rounded-3 ms-sm-auto'
                      onClick={nextStep}
                    >
                      Selanjutnya <i className='bi bi-arrow-right ms-2'></i>
                    </button>
                  )
                ) : (
                  <div className='ms-sm-auto d-flex flex-column-reverse flex-sm-row gap-2 align-items-stretch align-items-sm-center'>
                    <button
                      type='button'
                      className='btn btn-outline-primary px-4 py-2 rounded-3 d-flex justify-content-center align-items-center gap-2'
                      onClick={handleSaveDraft}
                      disabled={isSavingDraft || isSubmittingSurvey}
                    >
                      {isSavingDraft && (
                        <span className='spinner-border spinner-border-sm'></span>
                      )}
                      Simpan Draft
                    </button>
                    <button
                      type='submit'
                      className='btn btn-success text-white px-5 py-2 rounded-3 d-flex justify-content-center align-items-center gap-2 shadow-sm fw-bold'
                      disabled={isSavingDraft || isSubmittingSurvey}
                    >
                      {isSubmittingSurvey ? (
                        <>
                          <span className='spinner-border spinner-border-sm'></span>
                          Mengirim...
                        </>
                      ) : (
                        <>
                          Submit Survei{' '}
                          <i className='bi bi-check-circle ms-2'></i>
                        </>
                      )}
                    </button>
                  </div>
                )}
              </div>
            </form>
          )}

          {/* Submit Confirmation Modal */}
          {showSubmitConfirm && (
            <>
              <div
                className='modal-backdrop fade show'
                style={{ zIndex: 1050 }}
              ></div>
              <div
                className='modal fade show d-block'
                tabIndex='-1'
                style={{ zIndex: 1055 }}
              >
                <div className='modal-dialog modal-dialog-centered'>
                  <div className='modal-content border-0 shadow-lg rounded-4 overflow-hidden'>
                    <div className='modal-body p-5 text-center bg-white'>
                      <div className='mb-4 text-primary'>
                        <div
                          className='bg-primary bg-opacity-10 d-inline-flex align-items-center justify-content-center rounded-circle'
                          style={{ width: '80px', height: '80px' }}
                        >
                          <i className='bi bi-send-check-fill display-4'></i>
                        </div>
                      </div>
                      <h4 className='fw-bold text-dark mb-3'>
                        Konfirmasi Submit
                      </h4>
                      <p className='text-muted mb-4'>
                        Apakah Anda yakin ingin mensubmit survei ini? Data yang
                        sudah disubmit tidak dapat diubah kembali.
                      </p>
                      <div className='d-flex gap-2 justify-content-center'>
                        <button
                          type='button'
                          className='btn btn-light px-4 rounded-pill fw-bold'
                          onClick={() => {
                            setShowSubmitConfirm(false);
                            showToast('Submit dibatalkan', 'info');
                          }}
                        >
                          Batal
                        </button>
                        <button
                          type='button'
                          className='btn btn-primary px-4 rounded-pill fw-bold'
                          onClick={() => {
                            setShowSubmitConfirm(false);
                            handleSubmit((data) => onSubmit(data, true))();
                          }}
                        >
                          Ya, Submit Sekarang
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Anomaly Detection Modal */}
          {anomalyAlert.show && (
            <>
              <div
                className='modal-backdrop fade show'
                style={{ zIndex: 1050 }}
              ></div>
              <div
                className='modal fade show d-block'
                tabIndex='-1'
                style={{ zIndex: 1055 }}
              >
                <div className='modal-dialog modal-dialog-centered modal-lg'>
                  <div className='modal-content border-0 shadow-lg rounded-4 overflow-hidden bg-white'>
                    <div className='modal-header bg-warning bg-opacity-10 border-0 p-4'>
                      <div className='d-flex align-items-center'>
                        <div
                          className='bg-warning bg-opacity-25 rounded-circle d-flex align-items-center justify-content-center me-3'
                          style={{ width: '48px', height: '48px' }}
                        >
                          <i className='bi bi-exclamation-triangle-fill text-warning fs-3'></i>
                        </div>
                        <h5 className='modal-title fw-bold text-dark mb-0'>
                          Peringatan Validasi Sistem
                        </h5>
                      </div>
                      <button
                        type='button'
                        className='btn-close'
                        onClick={() => {
                          setAnomalyAlert({ show: false, anomalies: [] });
                          setIsAnomalyConfirmed(false);
                        }}
                      ></button>
                    </div>
                    <div className='modal-body p-4'>
                      <div className='alert alert-warning border-0 bg-warning bg-opacity-10 text-dark mb-4 rounded-3'>
                        <p className='mb-0 fw-medium'>
                          Sistem mendeteksi beberapa pola pengisian yang tidak
                          wajar dalam survei Anda. Mohon tinjau kembali daftar
                          berikut:
                        </p>
                      </div>

                      <div
                        className='anomalies-list overflow-auto px-1'
                        style={{ maxHeight: '350px' }}
                      >
                        {anomalyAlert.anomalies.map((anno, idx) => (
                          <div
                            key={idx}
                            className={`p-3 rounded-4 mb-3 border-start border-4 ${anno.severity === 'MERAH' ? 'bg-danger bg-opacity-10 border-danger' : 'bg-warning bg-opacity-10 border-warning'}`}
                          >
                            <div className='d-flex align-items-start'>
                              <span
                                className={`badge rounded-pill me-2 mt-1 ${anno.severity === 'MERAH' ? 'bg-danger' : 'bg-warning text-dark'}`}
                              >
                                {anno.severity}
                              </span>
                              <div>
                                <h6 className='fw-bold mb-1 text-dark'>
                                  {anno.title} ({anno.code})
                                </h6>
                                <p className='small text-muted mb-0 leading-relaxed font-italic'>
                                  "{anno.warningMessage}"
                                </p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>

                      <div className='bg-light p-3 rounded-4 mt-2'>
                        <div className='form-check'>
                          <input
                            className='form-check-input'
                            type='checkbox'
                            id='confirmAnomalies'
                            checked={isAnomalyConfirmed}
                            onChange={(e) =>
                              setIsAnomalyConfirmed(e.target.checked)
                            }
                          />
                          <label
                            className='form-check-label small text-muted'
                            htmlFor='confirmAnomalies'
                          >
                            Saya mengonfirmasi bahwa data yang saya masukkan
                            adalah hasil observasi/wawancara aktual di lapangan
                            meskipun sistem mendeteksi anomali.
                          </label>
                        </div>
                      </div>
                    </div>
                    <div className='modal-footer border-0 bg-light p-3 gap-2 justify-content-end'>
                      <button
                        type='button'
                        className='btn btn-light px-4 rounded-3 fw-bold'
                        onClick={() => {
                          setAnomalyAlert({ show: false, anomalies: [] });
                          setIsAnomalyConfirmed(false);
                        }}
                      >
                        Tinjau Ulang Jawaban
                      </button>
                      <button
                        type='button'
                        id='btnAnomalyAction'
                        className='btn btn-warning px-4 rounded-3 fw-bold'
                        disabled={!isAnomalyConfirmed}
                        onClick={handleAnomalyAction}
                      >
                        {anomalyAlert.mode === 'SECTION'
                          ? 'Ya, Tetap Lanjut'
                          : 'Ya, Tetap Simpan'}{' '}
                        <i
                          className={`bi ${anomalyAlert.mode === 'SECTION' ? 'bi-arrow-right' : 'bi-send'} ms-2`}
                        ></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default BumdesVeci;
